import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { qk } from "@/lib/query-keys";
import { toastErrorApi, toastExito } from "@/lib/toast";
import {
  asignarPermisosARol,
  crearRol,
  editarRol,
  eliminarRol,
  type CrearRolInput,
  type EditarRolInput,
} from "../services/roles-service";

function invalidar(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: qk.adminRoles });
  // Los roles alimentan el selector de roles del gestor de usuarios.
  void qc.invalidateQueries({ queryKey: qk.adminUsuarios });
}

export function useCrearRol(opts?: { onCreado?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CrearRolInput) => crearRol(input),
    onSuccess: (rol) => {
      toastExito({
        title: `Rol '${rol.nombre}' creado`,
        message: "Es una etiqueta asignable a usuarios · no otorga permisos hasta cablearse en el código.",
      });
      invalidar(qc);
      opts?.onCreado?.();
    },
    onError: toastErrorApi,
  });
}

export function useEditarRol(opts?: { onEditado?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: EditarRolInput }) => editarRol(id, input),
    onSuccess: () => {
      toastExito({ title: "Rol actualizado" });
      invalidar(qc);
      opts?.onEditado?.();
    },
    onError: toastErrorApi,
  });
}

export function useAsignarPermisos(opts?: { onAsignado?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, permisos }: { id: string; permisos: string[] }) => asignarPermisosARol(id, permisos),
    onSuccess: (rol) => {
      toastExito({
        title: `Permisos de '${rol.nombre}' actualizados`,
        message: "Aplican al instante en el backend · en la UI del usuario, tras su próximo login.",
      });
      invalidar(qc);
      opts?.onAsignado?.();
    },
    onError: toastErrorApi,
  });
}

export function useEliminarRol() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => eliminarRol(id),
    onSuccess: () => {
      toastExito({ title: "Rol eliminado" });
      invalidar(qc);
    },
    onError: toastErrorApi,
  });
}
